"use client"

import { FormikProvider, useFormik } from "formik"
import { useMutation } from "@tanstack/react-query"
import { FileText, Loader2, PencilLine, Sparkles } from "lucide-react"

import { OnboardingStepActions } from "@/components/onboarding/onboarding-step-actions"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/hooks/use-auth"
import { withQueryHandling } from "@/lib/with-query-handling"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type ManualKnowledgeFormProps = {
  isDefault?: boolean
  onDocumentCreated?: (id: string) => void | Promise<void>
  balanceDepleted?: boolean
  onImported: () => void | Promise<void>
}

type ManualKnowledgeFormValues = {
  content: string
}

const MANUAL_KNOWLEDGE_TITLE = "Business information"

export function ManualKnowledgeForm({
  isDefault = true,
  onDocumentCreated,
  balanceDepleted = false,
  onImported,
}: ManualKnowledgeFormProps) {

  const tUi = useTranslations("Onboarding.ManualKnowledgeForm")

  const { fetchWithAuth } = useAuth()
  const createDocument = useMutation({
    mutationFn: (content: string) => withQueryHandling(
      createManualKnowledgeDocument(fetchWithAuth, content, isDefault),
      {
        loading: tUi("addingYourBusinessInformation"),
        success: tUi("businessInformationAdded"),
      },
    ),
  })
  const formik = useFormik<ManualKnowledgeFormValues>({
    initialValues: { content: "" },
    validate: ({ content }) => (
      content.trim() ? {} : { content: tUi("enterSomeBusinessInformation") }
    ),
    onSubmit: async ({ content }, helpers) => {
      try {
        const result = await createDocument.mutateAsync(content)
        await onDocumentCreated?.(result.document.id)
        await onImported()
      } catch (error) {
        helpers.setStatus(
          error instanceof Error
            ? error.message
            : tUi("couldNotAddBusinessInformation"),
        )
      }
    },
  })
  const contentError = formik.touched.content && formik.errors.content
    ? formik.errors.content
    : null
  const isSubmitting = formik.isSubmitting || createDocument.isPending

  return (
    <Card className="overflow-hidden border-blue-200 bg-gradient-to-br from-blue-50 via-white to-violet-50">
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <CardContent className="p-0">
            <div className="grid gap-6 p-4 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)] lg:items-start">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span key={tUi("textSetup")}>{tUi("textSetup")}</span></div>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold tracking-tight">
                    <span key={tUi("tellUsAboutYourBusiness")}>{tUi("tellUsAboutYourBusiness")}</span></h2>
                  <p className="max-w-2xl text-sm text-muted-foreground">
                    <span key={tUi("pasteOrWriteKeyDetailsSuchAsYour")}>{tUi("pasteOrWriteKeyDetailsSuchAsYour")}</span></p>
                  {balanceDepleted ? (
                    <p className="text-sm font-medium text-amber-700">
                      <span key={tUi("topUpYourBalanceAfterThisStepSo")}>{tUi("topUpYourBalanceAfterThisStepSo")}</span></p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1"><TranslationText>{tUi.rich("tag0Tag0AddInformationDirectly", { tag0: () => (<PencilLine className="h-3.5 w-3.5" />) })}</TranslationText></span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1">
                    <FileText className="h-3.5 w-3.5" />
                    <span key={tUi("savedAsKnowledge")}>{tUi("savedAsKnowledge")}</span></span>
                </div>
              </div>

              <div className="space-y-2 rounded-lg border bg-white p-4 shadow-sm">
                <Label htmlFor="onboarding-manual-knowledge">
                  <span key={tUi("businessInformation")}>{tUi("businessInformation")}</span></Label>
                <Textarea
                  id="onboarding-manual-knowledge"
                  name="content"
                  value={formik.values.content}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  aria-invalid={Boolean(contentError)}
                  placeholder={tUi("forExampleWeProvideOurOpeningHoursAre")}
                  className="min-h-40 resize-y"
                />
                <p key={contentError || tUi("includeAnythingYourAgentsShouldKnowWhenHelping")} className={contentError ? "text-xs font-medium text-destructive" : "text-xs text-muted-foreground"}>
                  {contentError || tUi("includeAnythingYourAgentsShouldKnowWhenHelping")}
                </p>
                {formik.status ? (
                  <p className="text-sm font-medium text-destructive">
                    {String(formik.status)}
                  </p>
                ) : null}
              </div>
            </div>

            <OnboardingStepActions className="border-blue-100 bg-white/80 px-6 py-4">
              <Button
                type="submit"
                disabled={isSubmitting || !formik.values.content.trim()}
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                <span><span key={tUi("addInformation")}>{tUi("addInformation")}</span></span>
              </Button>
            </OnboardingStepActions>
          </CardContent>
        </form>
      </FormikProvider>
    </Card>
  )
}

async function createManualKnowledgeDocument(
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>,
  content: string,
  isDefault: boolean,
): Promise<{ document: { id: string } }> {
  const response = await fetchWithAuth("/api/ai/knowledge-base/documents", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title: MANUAL_KNOWLEDGE_TITLE,
      content: content.trim(),
      content_type: "text/plain",
      source_type: "manual",
      injection_mode: "auto",
      is_default: isDefault,
    }),
  })

  if (response.ok) return response.json() as Promise<{ document: { id: string } }>

  const error = (await response.json().catch(() => ({}))) as { error?: string }
  throw new Error(error.error || "Failed to add business information")
}
